import { point } from "@turf/helpers";
import bearing from "@turf/bearing";
import { pixelDistance } from "./utils/MapUtils";
import { LngLat, LngLatLike, Map } from "maplibre-gl";
import { copyLocProps } from "./utils/Utils";
import { getIcon } from "./shared/Icons";
import { ArrowData, ComputedMarker } from './shared/types/zod';
import { TFeature, TFeatureCollection, TLineString, TPoint } from './shared/types/geojson';
import { getLineIndex } from "./utils/lineSampling";

// Unit conversion constants
export const KM_TO_MILES = 1.609344;
export const KM_TO_100M = 10;
export const PACE_100M_TO_100YD = 100 / 109.36; // Convert min/100m to min/100yd (100yd is shorter)

export function getKmMarkersAlong(line: TLineString, step = 1, miles = false, raceLength?: number): TFeatureCollection {

	const index = getLineIndex(line.geometry);

	const len: number = index.length;
	if (!raceLength)
		raceLength = len;

	//if the track does not have the actual length of the race, let's stretch the km markers to make the length look more real. 
	const lengthPerKm = len / raceLength;

	let dist: number = step;

	const milesMult = miles ? KM_TO_MILES : 1;
	const numMarkers = raceLength / milesMult;
	const r: Array<TFeature> = [];

	while (dist < numMarkers) {
		const place = dist * lengthPerKm * milesMult;
		const pt: TFeature = index.sampleFeatureAtDistance(place) as TFeature;

		let zoom = 12.5;
		if (dist % 5 == 0)
			zoom = 10.5;
		if (dist % 10 == 0)
			zoom = 8;
		if (dist % 50 == 0)
			zoom = 5;

		pt.id = dist;

		pt.properties = {
			text: dist.toString(),
			__progress: [place / len],
			label: `${dist} ${miles ? "mi" : "km"}`,
			icon: "km",
			//anchor: "bottom-right",
			arrow: true,
			z: 0,
			__generated: true,
			zoom,
		};
		r.push(pt);
		dist += step;
	}

	return {
		type: "FeatureCollection",
		features: r,
	};
}

export function getAutoMarkers(track: TLineString, trackLength: number, languageSuffixes: string[]): ComputedMarker[] {

	if (track.geometry.coordinates.length < 1)
		return [];

	const markers: ComputedMarker[] = [];
	let start: ComputedMarker | undefined;
	let goal: ComputedMarker | undefined;

	if (track.geometry.coordinates[0][0] != 0) {
		start = point(track.geometry.coordinates[0]) as ComputedMarker;
		start.id = `start-${track.id}`;
		start.properties = {
			...copyLocProps(track.properties!, "startLabel", languageSuffixes, "text"),
			...copyLocProps(track.properties!, "startLabel", languageSuffixes, "label"),
			...copyLocProps(track.properties!, "startDescription", languageSuffixes, "description"),
			__progress: [0],
			icon: track.properties?.icon,
			anchor: track.properties?.icon == "start" || track.properties?.icon.includes("oslo") ? "bottom" : undefined,
			__generated: true,
			color: track.properties?.color ?? "white",
			// broke skibirken
			// __zIndex: 3,
		};
		markers.push(start);
	}

	const goalIcon = getIcon(track.properties?.icon + "-goal");

	if (trackLength > 0.05) {
		goal = point(track.geometry.coordinates[track.geometry.coordinates.length - 1]) as ComputedMarker;
		goal.id = `goal-${track.id}`;
		goal.properties = {
			...copyLocProps(track.properties!, "goalLabel", languageSuffixes, "text"),
			...copyLocProps(track.properties!, "goalLabel", languageSuffixes, "label"),
			...copyLocProps(track.properties!, "goalDescription", languageSuffixes, "description"),
			__progress: [1],
			icon: goalIcon.id ? goalIcon.id : track.properties?.icon,
			anchor: track.properties?.icon == "start" || track.properties?.icon.includes("oslo") ? "bottom" : undefined,
			__generated: true,
			color: track.properties?.color ?? "white",
			// __zIndex: 3,
		};
		markers.push(goal)
	}

	if (start && goal) {
		const marker = start.geometry.coordinates[0] < goal.geometry.coordinates[0] ? start : goal;
		marker.properties.__forceLabelAnchorRight = true;
	}

	return markers;
}

export function getArrowMarkersAlong(line: TLineString, miles: boolean = false): TFeatureCollection {

	const index = getLineIndex(line.geometry);
	const raceLength: number = index.length;

	let r: TFeature[] = [];

	const arrows: ArrowData[] = line.properties?.arrows ?? [];

	for (const arrow of arrows) {
		let distance = Number.parseFloat(arrow.distance as unknown as string);
		if (Number.isNaN(distance) || distance < 0.005)
			distance = 0.005;

		if (miles)
			distance *= KM_TO_MILES;

		const pt: TPoint = index.sampleFeatureAtDistance(distance) as TPoint;
		const behind: TPoint = index.sampleFeatureAtDistance(distance - 0.005) as TPoint;

		let zoom = 9;

		pt.id = Math.random();

		pt.properties = {
			position: arrow.position,
			progress: distance / raceLength,
			cat: "track-arrow",
			z: 0,
			zoom,
			angle: bearing(pt.geometry!.coordinates, behind.geometry!.coordinates) + 180
		};
		r.push(pt);
	}

	return {
		type: "FeatureCollection",
		features: r,
	};
}


export function snapToTrack(coords: [number, number], track: TLineString, map: Map): [number, number] {
	const pt = getLineIndex(track.geometry).nearest(coords);
	if (!pt) return coords;
	const distance = pixelDistance(pt.coordinates as LngLatLike, LngLat.convert(coords), map);
	if (distance < 15)
		return pt.coordinates;
	return coords;

}